import React, { useEffect, useMemo } from "react";
import { Box, Skeleton } from "@mui/material";
import {
  DescriptionOutlined,
  OpenInFullOutlined,
  ShareOutlined,
  PictureAsPdfOutlined,
  AttachFileOutlined,
  CheckCircleOutline,
  StickyNote2Outlined,
  AccessTimeOutlined,
} from "@mui/icons-material";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import { getMachine } from "../../customerMachinerySlice";
import {
  getServiceReport,
  getMachineParts,
} from "../../customerMachineServiceReportsSlice";
import { CustomerMachineState } from "../../models/models";
import {
  ServiceReport,
  ServiceReportStatusCode,
  ServiceReportFileTypeCode,
  ServiceReportsState,
} from "../../models/ServiceReportsModel";
import { useRedesignSetup } from "../../../../shared/hooks/useRedesignSetup";
import { useBreakpoint } from "../../../../shared/hooks/useBreakpoint";
import { useGalleryImages, ImagePreviewStrip } from "../../../../shared/components/gallery";
import ReportFile from "../../../../shared/ReportFile";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { getMachineDisplayName } from "../../../../shared/utils/machineUtils";
import { dt as sharedDt } from "../../../../shared/themes/redesignTokens";
import RedesignStatusBadge from "../../../../shared/components/redesign/RedesignStatusBadge";
import { dt } from "../../utils/dt";
import SrHeaderStrip from "./SrHeaderStrip";
import SrMachineCard from "./SrMachineCard";
import SrProblemCard from "./SrProblemCard";
import SrSolutionCard from "./SrSolutionCard";
import SrCostCard from "./SrCostCard";
import SrPeopleCard from "./SrPeopleCard";
import SrActionBar from "./SrActionBar";

// ===================================================================
//  DURATION HELPER
// ===================================================================
function computeDuration(from: Date | string, to: Date | string): string {
  const start = new Date(from).getTime();
  const end = new Date(to).getTime();
  const diffMs = Math.abs(end - start);
  const totalMinutes = Math.floor(diffMs / 60000);
  const totalHours = Math.floor(totalMinutes / 60);
  const days = Math.floor(totalHours / 24);
  const hours = totalHours % 24;

  if (days === 0 && hours === 0) return "< 1h";
  if (days === 0) return `${hours}h`;
  if (hours === 0) return `${days}d`;
  return `${days}d ${hours}h`;
}

function formatDateTime(d?: Date | string): string {
  if (!d) return "";
  const date = new Date(d);
  const dd = date.getDate();
  const mm = date.getMonth() + 1;
  const yyyy = date.getFullYear();
  const hh = String(date.getHours()).padStart(2, "0");
  const mi = String(date.getMinutes()).padStart(2, "0");
  return `${dd}.${mm}.${yyyy}, ${hh}:${mi}`;
}

// ===================================================================
//  PROPS
// ===================================================================
export interface ServiceReportDetailViewProps {
  reportNumber: string;
  machineId: string;
  compact?: boolean;
  preloadedReport?: ServiceReport;
  onOpenFullPage?: () => void;

  // Non-compact action handlers (wired by SrDetailPage wrapper)
  onEdit?: () => void;
  onSolve?: () => void;
  onShare?: () => void;
  onDownloadPdf?: () => void;
  onCreateServiceRequest?: () => void;
  onBack?: () => void;

  // Non-compact permission gates
  canEdit?: boolean;
  canSolve?: boolean;
  canCreateSR?: boolean;
}

// ===================================================================
//  MAIN COMPONENT
// ===================================================================
const ServiceReportDetailView = ({
  reportNumber,
  machineId,
  compact = false,
  preloadedReport,
  onOpenFullPage,
  onEdit,
  onSolve,
  onShare,
  onDownloadPdf,
  onCreateServiceRequest,
  onBack,
  canEdit = false,
  canSolve = false,
  canCreateSR = false,
}: ServiceReportDetailViewProps) => {
  useRedesignSetup();
  const dispatch = useAppDispatch();
  const { translate } = useTranslation("myMachinery");

  // Breakpoints: compact mode forces phone-style single column regardless of viewport
  const { isPhone: vpPhone, isTablet: vpTablet, isDesktop: vpDesktop } = useBreakpoint();
  const isPhone = compact ? true : vpPhone;
  const isTablet = compact ? false : vpTablet;
  const isDesktop = compact ? false : vpDesktop;

  const { machine } = useAppSelector<CustomerMachineState>(
    (state: any) => state.customerMachinery
  );
  const { serviceReport: reduxReport, machineParts, loading } =
    useAppSelector<ServiceReportsState>(
      (state: any) => state.customerServiceReports
    );

  // Resolve the SR to render: preloaded prop OR redux
  const serviceReport = preloadedReport || reduxReport;

  // Load data unless preloaded
  useEffect(() => {
    if (preloadedReport) return;
    if (machineId) dispatch(getMachine(machineId));
  }, [machineId, preloadedReport]);

  useEffect(() => {
    if (preloadedReport) return;
    if (reportNumber) dispatch(getServiceReport(reportNumber));
    dispatch(getMachineParts());
  }, [reportNumber, preloadedReport]);

  // Derived data
  const isSolved =
    serviceReport?.statusCode === ServiceReportStatusCode.solved;

  const categoryName = useMemo(() => {
    if (!serviceReport?.affectedPartCategoryId || !machineParts) return undefined;
    for (const group of machineParts) {
      const part = group.parts?.find((p) => p.id === serviceReport.affectedPartCategoryId);
      if (part) return part.name;
    }
    return undefined;
  }, [machineParts, serviceReport?.affectedPartCategoryId]);

  const displayName = machine ? getMachineDisplayName(machine) : "";

  const resolutionText = useMemo(() => {
    if (!serviceReport?.openDate) return null;
    if (isSolved && serviceReport?.closeDate) {
      const dur = computeDuration(
        serviceReport.openDate,
        serviceReport.closeDate
      );
      return translate("srDetail.resolvedIn", { duration: dur });
    }
    const dur = computeDuration(serviceReport.openDate, new Date());
    return translate("srDetail.openFor", { duration: dur });
  }, [serviceReport?.openDate, serviceReport?.closeDate, isSolved]);

  // File splitting (shared by both modes)
  const reportFiles = useMemo(
    () =>
      serviceReport?.files?.filter(
        (f) => f.typeCode === ServiceReportFileTypeCode.reportDocument
      ) || [],
    [serviceReport?.files]
  );
  const solvingFiles = useMemo(
    () =>
      serviceReport?.files?.filter(
        (f) => f.typeCode === ServiceReportFileTypeCode.solvingReportDocument
      ) || [],
    [serviceReport?.files]
  );

  const reportImageFiles = useGalleryImages({
    type: "entityFiles",
    files: reportFiles.filter(
      (f) => f.content && f.name
    ) as Array<{ name: string; content: string; size?: number }>,
  });
  const reportNonImageFiles = reportFiles.filter(
    (f) =>
      !f.content?.startsWith("data:image/") &&
      !/\.(jpg|jpeg|png|gif|webp|svg)$/i.test(f.name || "")
  );

  const solvingImageFiles = useGalleryImages({
    type: "entityFiles",
    files: solvingFiles.filter(
      (f) => f.content && f.name
    ) as Array<{ name: string; content: string; size?: number }>,
  });
  const solvingNonImageFiles = solvingFiles.filter(
    (f) =>
      !f.content?.startsWith("data:image/") &&
      !/\.(jpg|jpeg|png|gif|webp|svg)$/i.test(f.name || "")
  );

  // ===================================================================
  //  LOADING STATE
  // ===================================================================
  if (loading && !serviceReport) {
    if (compact) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: "20px 24px" }}>
          <Skeleton variant="text" width={140} height={16} />
          <Skeleton variant="text" width="80%" height={32} />
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: sharedDt.radius }} />
          <Skeleton variant="rectangular" height={100} sx={{ borderRadius: sharedDt.radius }} />
        </Box>
      );
    }
    return (
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: isPhone ? "1fr" : "25% 50% 25%",
          gap: 2.5,
          maxWidth: 1280,
          margin: "0 auto",
          px: isPhone ? 1.5 : 3,
          pb: 3,
        }}
      >
        <Skeleton
          variant="rectangular"
          height={280}
          sx={{ borderRadius: dt.machinery.card.radius }}
        />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Skeleton variant="rectangular" height={64} sx={{ borderRadius: dt.radiusSm }} />
          <Skeleton variant="rectangular" height={200} sx={{ borderRadius: dt.machinery.card.radius }} />
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Skeleton variant="rectangular" height={160} sx={{ borderRadius: dt.machinery.card.radius }} />
          <Skeleton variant="rectangular" height={120} sx={{ borderRadius: dt.machinery.card.radius }} />
        </Box>
      </Box>
    );
  }

  // ===================================================================
  //  NOT FOUND STATE
  // ===================================================================
  if (!loading && !serviceReport) {
    if (compact) {
      return (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: "12px",
            p: "48px 24px",
            fontFamily: sharedDt.font,
          }}
        >
          <DescriptionOutlined sx={{ fontSize: "48px", color: sharedDt.textMuted }} />
          <Box
            sx={{
              fontSize: "15px",
              fontWeight: 600,
              color: sharedDt.textPrimary,
              textAlign: "center",
            }}
          >
            {translate("srDetail.notFound")}
          </Box>
          {onOpenFullPage && (
            <Box
              onClick={onOpenFullPage}
              sx={{
                fontSize: "13px",
                color: sharedDt.blue,
                cursor: "pointer",
                "&:hover": { textDecoration: "underline" },
              }}
            >
              {translate("srDetail.backToMachine")}
            </Box>
          )}
        </Box>
      );
    }
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: "400px",
          gap: "16px",
        }}
      >
        <DescriptionOutlined sx={{ fontSize: "64px", color: dt.textMuted }} />
        <Box
          sx={{
            fontSize: "20px",
            fontWeight: 600,
            fontFamily: dt.font,
            color: dt.textPrimary,
          }}
        >
          {translate("srDetail.notFound")}
        </Box>
        {onBack && (
          <Box
            onClick={onBack}
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              fontFamily: dt.font,
              color: dt.blue,
              cursor: "pointer",
              "&:hover": { textDecoration: "underline" },
            }}
          >
            {translate("srDetail.backToMachine")}
          </Box>
        )}
      </Box>
    );
  }

  if (!serviceReport) return null;

  // ===================================================================
  //  COMPACT MODE (F4/F5/F6 — artifact pane)
  // ===================================================================
  if (compact) {
    const statusLabel = isSolved
      ? translate("serviceReportStatus.solved")
      : translate("serviceReportStatus.inProgress");
    const statusColor = isSolved
      ? dt.serviceReports.status.solved
      : dt.serviceReports.status.inProgress;

    const machineLabel = displayName ||
      (machine ? translate("machine") : "");

    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "22px",
          padding: "24px 28px 28px",
          fontFamily: sharedDt.font,
          color: sharedDt.textPrimary,
          backgroundColor: sharedDt.cardBg,
        }}
      >
        {/* === HEADER BLOCK === */}
        <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
          {/* SR number (mono, gold) */}
          <Box
            sx={{
              fontFamily: sharedDt.fontMono,
              fontSize: "11px",
              fontWeight: 600,
              color: "#D4A017",
              letterSpacing: "0.02em",
            }}
          >
            #{serviceReport.number}
          </Box>

          {/* Status pill + resolution chip */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <RedesignStatusBadge label={statusLabel} colorConfig={statusColor} />
            {resolutionText && (
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "5px",
                  fontSize: "11px",
                  fontWeight: 600,
                  fontFamily: sharedDt.fontMono,
                  color: isSolved ? "#1E7E45" : sharedDt.blueHover,
                  backgroundColor: isSolved
                    ? "rgba(39,174,96,0.06)"
                    : "rgba(52,152,219,0.06)",
                  padding: "3px 9px",
                  borderRadius: "12px",
                }}
              >
                <AccessTimeOutlined sx={{ fontSize: 13 }} />
                {resolutionText}
              </Box>
            )}
          </Box>

          {/* Title — Sora 24 */}
          <Box
            sx={{
              fontFamily: sharedDt.font,
              fontSize: "24px",
              fontWeight: 600,
              color: sharedDt.textPrimary,
              lineHeight: 1.3,
              letterSpacing: "-0.02em",
            }}
          >
            {serviceReport.title}
          </Box>

          {/* Meta row: Vytvorené / NAHLÁSIL / Stroj */}
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: "6px 14px",
              fontSize: "12px",
              fontFamily: sharedDt.font,
              color: sharedDt.textSecondary,
              mt: "2px",
            }}
          >
            {serviceReport.openDate && (
              <MetaItem
                label={translate("srDetail.createdShort")}
                value={formatDateTime(serviceReport.openDate)}
              />
            )}
            {serviceReport.createdByName && (
              <MetaItem
                label={translate("reportedBy")}
                value={
                  serviceReport.createdByRole
                    ? `${serviceReport.createdByName} (${serviceReport.createdByRole})`
                    : serviceReport.createdByName
                }
              />
            )}
            {machineLabel && (
              <MetaItem label={translate("machine")} value={machineLabel} />
            )}
          </Box>

          {/* Solved meta (when solved) */}
          {isSolved && (serviceReport.closeDate || serviceReport.solvedByName) && (
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                gap: "6px 14px",
                fontSize: "12px",
                fontFamily: sharedDt.font,
                color: sharedDt.textSecondary,
              }}
            >
              {serviceReport.solvedByName && (
                <MetaItem
                  label={translate("resolvedBy")}
                  value={
                    serviceReport.solvedByRole
                      ? `${serviceReport.solvedByName} (${serviceReport.solvedByRole})`
                      : serviceReport.solvedByName
                  }
                />
              )}
              {serviceReport.closeDate && (
                <MetaItem
                  label={translate("closeDate")}
                  value={formatDateTime(serviceReport.closeDate)}
                />
              )}
            </Box>
          )}
        </Box>

        {/* === PROBLEM SECTION === */}
        <CompactSection
          accentColor="#D4A017"
          label={translate("srDetail.problemDescription")}
          icon={<DescriptionOutlined sx={{ fontSize: 13 }} />}
        >
          {serviceReport.description ? (
            <CompactBody>{serviceReport.description}</CompactBody>
          ) : (
            <CompactMuted>{translate("srDetail.noDescription")}</CompactMuted>
          )}
          {(reportImageFiles.length > 0 || reportNonImageFiles.length > 0) && (
            <Box sx={{ mt: 1.5 }}>
              <AttachmentsStrip
                images={reportImageFiles}
                nonImages={reportNonImageFiles}
                label={translate("srDetail.reportAttachments")}
              />
            </Box>
          )}
        </CompactSection>

        {/* === NOTES SECTION (omit if empty) === */}
        {serviceReport.notes && (
          <CompactSection
            accentColor={sharedDt.textMuted}
            label={translate("srDetail.technicalNotes")}
            icon={<StickyNote2Outlined sx={{ fontSize: 13 }} />}
          >
            <CompactBody>{serviceReport.notes}</CompactBody>
          </CompactSection>
        )}

        {/* === SOLUTION SECTION === */}
        <CompactSection
          accentColor="#27AE60"
          label={translate("srDetail.solution")}
          icon={<CheckCircleOutline sx={{ fontSize: 13 }} />}
        >
          {isSolved && serviceReport.solvingDescription ? (
            <CompactBody>{serviceReport.solvingDescription}</CompactBody>
          ) : (
            <CompactMuted>
              {isSolved
                ? translate("srDetail.noSolutionDescription")
                : translate("srDetail.notYetSolved")}
            </CompactMuted>
          )}
          {(solvingImageFiles.length > 0 || solvingNonImageFiles.length > 0) && (
            <Box sx={{ mt: 1.5 }}>
              <AttachmentsStrip
                images={solvingImageFiles}
                nonImages={solvingNonImageFiles}
                label={translate("srDetail.solutionAttachments")}
              />
            </Box>
          )}
        </CompactSection>

        {/* === FOOTER ACTIONS === */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            pt: "8px",
            borderTop: `1px solid ${sharedDt.rowBorder}`,
          }}
        >
          {onOpenFullPage && (
            <CompactActionButton
              primary
              onClick={onOpenFullPage}
              icon={<OpenInFullOutlined sx={{ fontSize: 15 }} />}
              label={translate("srDetail.openFullPage")}
            />
          )}
          {onShare && (
            <CompactActionButton
              onClick={onShare}
              icon={<ShareOutlined sx={{ fontSize: 15 }} />}
              label={translate("srDetail.shareReport")}
            />
          )}
          {onDownloadPdf && (
            <CompactActionButton
              onClick={onDownloadPdf}
              icon={<PictureAsPdfOutlined sx={{ fontSize: 15 }} />}
              label="PDF"
            />
          )}
        </Box>
      </Box>
    );
  }

  // ===================================================================
  //  NON-COMPACT MODE — EXISTING LAYOUT PRESERVED AS-IS
  // ===================================================================

  // Desktop layout (25 / 50 / 25)
  const renderDesktopLayout = () => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "25% 50% 25%",
        gap: 2.5,
        maxWidth: 1280,
        margin: "0 auto",
        px: 3,
        pb: 3,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          animation: "sectionReveal 0.4s ease-out 0.1s both",
        }}
      >
        {machine && <SrMachineCard machine={machine} machineId={machineId} />}
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
          animation: "heroReveal 0.4s ease-out both",
        }}
      >
        <SrHeaderStrip
          serviceReport={serviceReport}
          resolutionText={resolutionText}
          isSolved={isSolved}
        />
        <SrProblemCard
          serviceReport={serviceReport}
          reportImageFiles={reportImageFiles}
          reportNonImageFiles={reportNonImageFiles}
          isPhone={false}
        />
        {isSolved && (
          <SrSolutionCard
            serviceReport={serviceReport}
            solvingImageFiles={solvingImageFiles}
            solvingNonImageFiles={solvingNonImageFiles}
            isPhone={false}
          />
        )}
        <SrActionBar
          onBack={onBack || (() => {})}
          onShare={onShare || (() => {})}
          onDownloadPdf={onDownloadPdf || (() => {})}
          onCreateServiceRequest={onCreateServiceRequest || (() => {})}
          onEdit={onEdit || (() => {})}
          onSolve={onSolve || (() => {})}
          isSolved={isSolved}
          isPhone={false}
          isTabletOrPhone={false}
          canEdit={canEdit}
          canSolve={canSolve}
          canCreateSR={canCreateSR}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 2,
          animation: "sectionReveal 0.4s ease-out 0.15s both",
        }}
      >
        <SrCostCard
          serviceReport={serviceReport}
          categoryName={categoryName}
          isPhone={false}
        />
        <SrPeopleCard serviceReport={serviceReport} isPhone={false} />
      </Box>
    </Box>
  );

  // Tablet layout
  const renderTabletLayout = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        maxWidth: 900,
        margin: "0 auto",
        px: 2,
        pb: 2,
      }}
    >
      <Box sx={{ animation: "heroReveal 0.4s ease-out both" }}>
        <SrHeaderStrip
          serviceReport={serviceReport}
          resolutionText={resolutionText}
          isSolved={isSolved}
        />
      </Box>
      <Box sx={{ animation: "heroReveal 0.4s ease-out 0.05s both" }}>
        <SrProblemCard
          serviceReport={serviceReport}
          reportImageFiles={reportImageFiles}
          reportNonImageFiles={reportNonImageFiles}
          isPhone={false}
        />
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 2,
          animation: "sectionReveal 0.4s ease-out 0.1s both",
        }}
      >
        {machine && <SrMachineCard machine={machine} machineId={machineId} />}
        <SrCostCard
          serviceReport={serviceReport}
          categoryName={categoryName}
          isPhone={false}
        />
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.12s both" }}>
        <SrPeopleCard serviceReport={serviceReport} isPhone={false} />
      </Box>
      {isSolved && (
        <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.15s both" }}>
          <SrSolutionCard
            serviceReport={serviceReport}
            solvingImageFiles={solvingImageFiles}
            solvingNonImageFiles={solvingNonImageFiles}
            isPhone={false}
          />
        </Box>
      )}
      <Box sx={{ height: 80 }} />
      <SrActionBar
        onBack={onBack || (() => {})}
        onShare={onShare || (() => {})}
        onDownloadPdf={onDownloadPdf || (() => {})}
        onCreateServiceRequest={onCreateServiceRequest || (() => {})}
        onEdit={onEdit || (() => {})}
        onSolve={onSolve || (() => {})}
        isSolved={isSolved}
        isPhone={false}
        isTabletOrPhone
        canEdit={canEdit}
        canSolve={canSolve}
        canCreateSR={canCreateSR}
      />
    </Box>
  );

  // Phone layout
  const renderPhoneLayout = () => (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 2,
        px: 1.5,
        pb: 1.5,
      }}
    >
      <Box sx={{ animation: "heroReveal 0.4s ease-out both" }}>
        <SrHeaderStrip
          serviceReport={serviceReport}
          resolutionText={resolutionText}
          isSolved={isSolved}
        />
      </Box>
      <Box sx={{ animation: "heroReveal 0.4s ease-out 0.05s both" }}>
        <SrProblemCard
          serviceReport={serviceReport}
          reportImageFiles={reportImageFiles}
          reportNonImageFiles={reportNonImageFiles}
          isPhone
        />
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.1s both" }}>
        {machine && <SrMachineCard machine={machine} machineId={machineId} />}
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.12s both" }}>
        <SrCostCard
          serviceReport={serviceReport}
          categoryName={categoryName}
          isPhone
        />
      </Box>
      <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.14s both" }}>
        <SrPeopleCard serviceReport={serviceReport} isPhone />
      </Box>
      {isSolved && (
        <Box sx={{ animation: "sectionReveal 0.4s ease-out 0.16s both" }}>
          <SrSolutionCard
            serviceReport={serviceReport}
            solvingImageFiles={solvingImageFiles}
            solvingNonImageFiles={solvingNonImageFiles}
            isPhone
          />
        </Box>
      )}
      <Box sx={{ height: 72 }} />
      <SrActionBar
        onBack={onBack || (() => {})}
        onShare={onShare || (() => {})}
        onDownloadPdf={onDownloadPdf || (() => {})}
        onCreateServiceRequest={onCreateServiceRequest || (() => {})}
        onEdit={onEdit || (() => {})}
        onSolve={onSolve || (() => {})}
        isSolved={isSolved}
        isPhone
        isTabletOrPhone
        canEdit={canEdit}
        canSolve={canSolve}
        canCreateSR={canCreateSR}
      />
    </Box>
  );

  return (
    <>
      {(isDesktop || (!isTablet && !isPhone)) && renderDesktopLayout()}
      {isTablet && renderTabletLayout()}
      {isPhone && renderPhoneLayout()}
    </>
  );
};

// ===================================================================
//  COMPACT HELPERS
// ===================================================================
const MetaItem = ({ label, value }: { label: string; value: string }) => (
  <Box sx={{ display: "inline-flex", alignItems: "baseline", gap: "5px" }}>
    <Box
      component="span"
      sx={{
        fontSize: "10px",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        color: sharedDt.textMuted,
      }}
    >
      {label}
    </Box>
    <Box
      component="span"
      sx={{
        fontSize: "12px",
        fontWeight: 500,
        color: sharedDt.textPrimary,
      }}
    >
      {value}
    </Box>
  </Box>
);

const CompactSection = ({
  accentColor,
  label,
  icon,
  children,
}: {
  accentColor: string;
  label: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Box>
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        mb: "10px",
      }}
    >
      <Box
        sx={{
          width: "3px",
          height: "14px",
          borderRadius: "2px",
          backgroundColor: accentColor,
        }}
      />
      {icon && <Box sx={{ color: accentColor, display: "inline-flex" }}>{icon}</Box>}
      <Box
        sx={{
          fontFamily: sharedDt.font,
          fontSize: "11px",
          fontWeight: 700,
          textTransform: "uppercase",
          letterSpacing: "0.1em",
          color: accentColor,
        }}
      >
        {label}
      </Box>
    </Box>
    {children}
  </Box>
);

const CompactBody = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      fontFamily: sharedDt.font,
      fontSize: "13.5px",
      color: "#374151",
      lineHeight: 1.6,
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
    }}
  >
    {children}
  </Box>
);

const CompactMuted = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      fontFamily: sharedDt.font,
      fontSize: "13px",
      color: sharedDt.textMuted,
      fontStyle: "italic",
    }}
  >
    {children}
  </Box>
);

const AttachmentsStrip = ({
  images,
  nonImages,
  label,
}: {
  images: any[];
  nonImages: any[];
  label: string;
}) => (
  <Box
    sx={{
      pt: 1.25,
      borderTop: `1px dashed ${sharedDt.rowBorder}`,
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: "6px",
        mb: 1,
        fontSize: "10px",
        fontWeight: 700,
        textTransform: "uppercase",
        letterSpacing: "0.08em",
        color: sharedDt.textMuted,
      }}
    >
      <AttachFileOutlined sx={{ fontSize: 12 }} />
      {label}
    </Box>
    {images.length > 0 && (
      <Box sx={{ mb: 1 }}>
        <ImagePreviewStrip images={images} maxVisible={4} thumbnailSize={56} />
      </Box>
    )}
    {nonImages.length > 0 && (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
        {nonImages.map((file: any, i: number) => (
          <ReportFile
            key={i}
            file={file}
            canDelete={false}
            canDownload={true}
            fileIndex={i}
          />
        ))}
      </Box>
    )}
  </Box>
);

const CompactActionButton = ({
  onClick,
  icon,
  label,
  primary,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  primary?: boolean;
}) => (
  <Box
    onClick={onClick}
    sx={{
      display: "inline-flex",
      alignItems: "center",
      gap: "6px",
      padding: "8px 14px",
      borderRadius: sharedDt.radiusSm,
      fontFamily: sharedDt.font,
      fontSize: "12.5px",
      fontWeight: 600,
      cursor: "pointer",
      transition: "all 0.18s cubic-bezier(0.4,0,0.2,1)",
      ...(primary
        ? {
            background: `linear-gradient(135deg, ${sharedDt.blue}, ${sharedDt.blueHover})`,
            color: "#FFFFFF",
            "&:hover": { filter: "brightness(1.05)" },
          }
        : {
            background: sharedDt.inputBg,
            color: sharedDt.textPrimary,
            border: `1px solid ${sharedDt.cardBorder}`,
            "&:hover": { backgroundColor: sharedDt.rowHover },
          }),
    }}
  >
    {icon}
    {label}
  </Box>
);

export default ServiceReportDetailView;
